import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Save, Type, Wrench, Award, Star, MessageSquare, Pencil, Plus, Trash2,
  GripVertical, Zap, BarChart3, ChevronUp, ChevronDown, Eye,
  Image as ImageIcon, Upload, Paintbrush, Package, Car, CalendarCheck,
  Monitor, Building2, PanelTop, MessageCircle, Menu, Link2, ImagePlus,
  BadgeCheck, FileText, Users, Briefcase, Phone, Mail, MapPin, AlertTriangle
} from "lucide-react";
import axios from "axios";
import { getImageUrl } from "@/utils/imageUrl";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import { QuickAccessEditor, IconItemEditor, ImageUploadField } from "@/components/admin/EditorComponents";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/* ═══════════════════════════════════════════
   HERO SLIDES EDITOR — Full slide management
   ═══════════════════════════════════════════ */
function HeroSlidesEditor() {
  const [slides, setSlides] = useState([]);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({});
  const [uploading, setUploading] = useState(false);
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/hero-slides`, { headers }); setSlides(r.data); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const activeCount = slides.filter(s => s.active).length;

  const createSlide = async () => {
    try { await axios.post(`${API}/hero-slides`, {}, { headers }); toast.success("Slide creado"); load(); } catch { toast.error("Error"); }
  };

  const deleteSlide = async (id) => {
    try { await axios.delete(`${API}/hero-slides/${id}`, { headers }); toast.success("Eliminado"); load(); } catch { toast.error("Error"); }
  };

  const toggleActive = async (s) => {
    try { await axios.put(`${API}/hero-slides/${s.id}`, { active: !s.active }, { headers }); load(); } catch {}
  };

  const openEdit = (s) => {
    setEditId(s.id);
    setForm({
      title: s.title||"", subtitle: s.subtitle||"", aux_text: s.aux_text||"",
      cta_primary_text: s.cta_primary_text||"", cta_primary_link: s.cta_primary_link||"",
      cta_secondary_text: s.cta_secondary_text||"", cta_secondary_link: s.cta_secondary_link||"",
      image_url: s.image_url||"", image_mobile_url: s.image_mobile_url||"",
      text_color: s.text_color||"white",
      overlay_opacity: s.overlay_opacity??70,
      // Iter 211: nuevo flag. Si no viene del backend, asumimos True (compatibilidad).
      overlay_enabled: s.overlay_enabled !== false,
      // Iter 214: modo de ajuste de imagen (cover/contain/auto). Default cover por compat.
      image_fit: s.image_fit || "cover",
      order: s.order??0, active: s.active??true,
    });
  };

  // Iter 211: parser de errores robusto. FastAPI puede devolver detail como
  // string o como array (validation errors). Si el backend devuelve un mensaje
  // claro, lo mostramos íntegro en lugar del genérico "Error al subir".
  const parseApiError = (e, fallback = "Error") => {
    const detail = e?.response?.data?.detail;
    if (!detail) {
      const status = e?.response?.status;
      const sizeHint = status === 413
        ? " (imagen demasiado grande; revisa el límite de Nginx/Caddy en producción)"
        : "";
      return (e?.message || fallback) + sizeHint;
    }
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map(d => d?.msg || JSON.stringify(d)).join(" | ");
    return JSON.stringify(detail);
  };

  const saveSlide = async () => {
    try {
      await axios.put(`${API}/hero-slides/${editId}`, form, { headers });
      toast.success("Guardado");
      setEditId(null);
      load();
    } catch (e) {
      toast.error(parseApiError(e, "Error al guardar"));
    }
  };

  const uploadImage = async (e, variant) => {
    const file = e.target.files?.[0]; if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["jpg","jpeg","png","webp"].includes(ext)) { toast.error("Formato no permitido"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await axios.post(`${API}/hero-slides/${editId}/image?variant=${variant}`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      const field = variant === "desktop" ? "image_url" : "image_mobile_url";
      setForm(p => ({ ...p, [field]: r.data.url }));
      toast.success("Imagen subida");
    } catch (err) {
      // Iter 211: mostramos el detail real del backend para diagnosticar
      // problemas de permisos/espacio/tamaño en producción.
      toast.error(parseApiError(err, "Error al subir"));
    }
    setUploading(false);
  };

  // Iter 213 — Eliminar imagen del slide (llama al nuevo DELETE del backend).
  // Pide confirmación al usuario porque la operación es destructiva (borra el
  // fichero del disco). Si el usuario cancela, no hace nada.
  const removeImage = async (variant) => {
    if (!editId) return;
    const variantLabel = variant === "desktop" ? "escritorio" : "móvil";
    if (!window.confirm(`¿Eliminar la imagen ${variantLabel} de este slide? Esta acción borrará el fichero del servidor.`)) return;
    try {
      await axios.delete(`${API}/hero-slides/${editId}/image/${variant}`, { headers });
      const field = variant === "desktop" ? "image_url" : "image_mobile_url";
      setForm(p => ({ ...p, [field]: "" }));
      toast.success(`Imagen ${variantLabel} eliminada`);
    } catch (e) {
      toast.error(parseApiError(e, `Error al eliminar la imagen ${variantLabel}`));
    }
  };

  const resolveImg = (url) => getImageUrl(url);

  const moveUp = async (i) => {
    if (i <= 0) return;
    const ids = slides.map(s => s.id);
    [ids[i-1], ids[i]] = [ids[i], ids[i-1]];
    try { await axios.put(`${API}/hero-slides/reorder`, { order: ids }, { headers }); load(); } catch {}
  };
  const moveDown = async (i) => {
    if (i >= slides.length - 1) return;
    const ids = slides.map(s => s.id);
    [ids[i], ids[i+1]] = [ids[i+1], ids[i]];
    try { await axios.put(`${API}/hero-slides/reorder`, { order: ids }, { headers }); load(); } catch {}
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-xs font-semibold text-slate-700 flex items-center gap-1.5"><Monitor className="w-3.5 h-3.5 text-red-600" /> Slides del Hero Principal</h4>
          <p className="text-[9px] text-slate-400 mt-0.5">{activeCount} slide{activeCount !== 1 ? "s" : ""} activo{activeCount !== 1 ? "s" : ""}. La home muestra estos slides cuando hay al menos 1 activo.</p>
        </div>
        <Button variant="outline" size="sm" onClick={createSlide} className="rounded-sm text-[10px] h-6 px-2 uppercase tracking-wider" data-testid="add-hero-slide"><Plus className="w-3 h-3 mr-1" /> Nuevo slide</Button>
      </div>

      {slides.length === 0 ? (
        <div className="border-2 border-dashed border-slate-200 rounded-sm p-6 text-center text-xs text-slate-400">No hay slides. La home usara el Hero Fallback (abajo).</div>
      ) : (
        <div className="space-y-1.5">
          {slides.map((s, i) => (
            <div key={s.id} className={`flex items-center gap-3 p-2 border rounded-sm group transition-all ${s.active ? "border-green-200 bg-green-50/30" : "border-slate-200 bg-slate-50/50 opacity-60"}`} data-testid={`hero-slide-${i}`}>
              <div className="w-20 h-12 rounded-sm overflow-hidden bg-slate-200 flex-shrink-0">
                {s.image_url ? <img src={resolveImg(s.image_url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-slate-800 flex items-center justify-center"><ImageIcon className="w-4 h-4 text-slate-500" /></div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-bold text-slate-900 truncate">{s.title || "Sin titulo"}</span>
                  {s.active ? <span className="text-[8px] uppercase tracking-wider font-bold text-green-600 bg-green-100 px-1.5 py-0.5 rounded-sm">Activo</span> : <span className="text-[8px] uppercase tracking-wider font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-sm">Inactivo</span>}
                  {i === 0 && s.active && <span className="text-[8px] uppercase tracking-wider font-bold text-red-600 bg-red-100 px-1.5 py-0.5 rounded-sm">Publicado</span>}
                </div>
                <span className="text-[10px] text-slate-400 truncate block">{s.subtitle?.slice(0, 60) || "Sin subtitulo"}</span>
              </div>
              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                <Button variant="ghost" size="sm" onClick={() => moveUp(i)} disabled={!i} className="h-6 w-6 p-0"><ChevronUp className="w-3 h-3" /></Button>
                <Button variant="ghost" size="sm" onClick={() => moveDown(i)} disabled={i >= slides.length-1} className="h-6 w-6 p-0"><ChevronDown className="w-3 h-3" /></Button>
                <Button variant="ghost" size="sm" onClick={() => openEdit(s)} className="h-6 w-6 p-0"><Pencil className="w-3 h-3" /></Button>
                <Switch checked={s.active} onCheckedChange={() => toggleActive(s)} data-testid={`slide-toggle-${i}`} />
                <Button variant="ghost" size="sm" onClick={() => deleteSlide(s.id)} className="h-6 w-6 p-0 text-red-500"><Trash2 className="w-3 h-3" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit slide dialog */}
      <Dialog open={editId !== null} onOpenChange={() => setEditId(null)}>
        <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto" data-testid="hero-slide-edit-dialog">
          <DialogHeader><DialogTitle className="heading-industrial text-sm uppercase">Editar Slide</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            {/* Live preview */}
            <div className="relative w-full h-36 rounded-sm overflow-hidden bg-slate-900" data-testid="slide-preview">
              {form.image_url && <img src={getImageUrl(form.image_url)} alt="" className="absolute inset-0 w-full h-full object-cover" />}
              <div className="absolute inset-0" style={{ background: `rgba(15,23,42,${(form.overlay_opacity||70)/100})` }} />
              <div className="absolute bottom-3 left-4 z-10">
                <p className="text-sm font-bold" style={{ color: form.text_color === "dark" ? "#0f172a" : "#ffffff" }}>{form.title || "Titulo del slide"}</p>
                <p className="text-[10px] mt-0.5" style={{ color: form.text_color === "dark" ? "#475569" : "#cbd5e1" }}>{(form.subtitle||"Subtitulo del slide").slice(0, 80)}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Titulo</Label>
                <Input value={form.title||""} onChange={e=>setForm(p=>({...p,title:e.target.value}))} className="h-8 text-xs rounded-sm" data-testid="slide-title" /></div>
              <div className="space-y-1"><Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Texto auxiliar</Label>
                <Input value={form.aux_text||""} onChange={e=>setForm(p=>({...p,aux_text:e.target.value}))} className="h-8 text-xs rounded-sm" /></div>
            </div>
            <div className="space-y-1"><Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Subtitulo</Label>
              <textarea value={form.subtitle||""} onChange={e=>setForm(p=>({...p,subtitle:e.target.value}))} rows={2} className="w-full border border-slate-200 rounded-sm text-xs p-2 resize-none" /></div>

            {/* Iter 213 — Imágenes responsive: desktop 16:9 + mobile vertical */}
            <div className="space-y-3">
              {/* Desktop */}
              <div className="space-y-2 p-3 border border-slate-200 rounded-sm bg-slate-50/50">
                <div className="flex items-center justify-between">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">
                    <ImageIcon className="w-3 h-3" /> Imagen escritorio (16:9)
                  </Label>
                  <span className="text-[9px] text-slate-400">Recomendado: 1536×864 px</span>
                </div>
                <div className="flex gap-2 items-center">
                  <Input
                    value={form.image_url || ""}
                    onChange={e => setForm(p => ({ ...p, image_url: e.target.value }))}
                    placeholder="URL o sube una imagen"
                    className="h-8 text-xs rounded-sm flex-1"
                    data-testid="slide-img-url"
                  />
                  <label className="flex items-center gap-1 px-3 h-8 text-[10px] font-semibold text-slate-600 border border-slate-300 rounded-sm cursor-pointer hover:bg-white transition-colors">
                    <Upload className="w-3 h-3" /> {uploading ? "..." : "Subir"}
                    <input type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={e => uploadImage(e, "desktop")} data-testid="slide-img-upload" />
                  </label>
                </div>
                {form.image_url && (
                  <div className="flex items-center gap-2 text-[9px] text-green-700">
                    <BadgeCheck className="w-3 h-3" /> Imagen configurada
                    <button
                      onClick={() => removeImage("desktop")}
                      className="text-red-500 underline ml-auto"
                      data-testid="slide-img-desktop-remove"
                    >Eliminar</button>
                  </div>
                )}
              </div>

              {/* Mobile */}
              <div className="space-y-2 p-3 border border-slate-200 rounded-sm bg-slate-50/50">
                <div className="flex items-center justify-between">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">
                    <ImageIcon className="w-3 h-3" /> Imagen móvil (vertical)
                  </Label>
                  <span className="text-[9px] text-slate-400">Recomendado: 1080×1920 px</span>
                </div>
                <div className="flex gap-2 items-center">
                  <Input
                    value={form.image_mobile_url || ""}
                    onChange={e => setForm(p => ({ ...p, image_mobile_url: e.target.value }))}
                    placeholder="URL o sube una imagen para móvil"
                    className="h-8 text-xs rounded-sm flex-1"
                    data-testid="slide-img-mobile-url"
                  />
                  <label className="flex items-center gap-1 px-3 h-8 text-[10px] font-semibold text-slate-600 border border-slate-300 rounded-sm cursor-pointer hover:bg-white transition-colors">
                    <Upload className="w-3 h-3" /> {uploading ? "..." : "Subir"}
                    <input type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={e => uploadImage(e, "mobile")} data-testid="slide-img-mobile-upload" />
                  </label>
                </div>
                {form.image_mobile_url ? (
                  <div className="flex items-center gap-2 text-[9px] text-green-700">
                    <BadgeCheck className="w-3 h-3" /> Imagen móvil configurada
                    <button
                      onClick={() => removeImage("mobile")}
                      className="text-red-500 underline ml-auto"
                      data-testid="slide-img-mobile-remove"
                    >Eliminar</button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 text-[9px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1" data-testid="slide-img-mobile-fallback-warning">
                    <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                    En móvil se usará la imagen de escritorio como fallback.
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">CTA Principal - Texto</Label>
                <Input value={form.cta_primary_text||""} onChange={e=>setForm(p=>({...p,cta_primary_text:e.target.value}))} placeholder="Reservar Cita" className="h-8 text-xs rounded-sm" /></div>
              <div className="space-y-1"><Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">CTA Principal - Enlace</Label>
                <Input value={form.cta_primary_link||""} onChange={e=>setForm(p=>({...p,cta_primary_link:e.target.value}))} placeholder="/cita" className="h-8 text-xs rounded-sm" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">CTA Secundario - Texto</Label>
                <Input value={form.cta_secondary_text||""} onChange={e=>setForm(p=>({...p,cta_secondary_text:e.target.value}))} placeholder="Ver Vehiculos" className="h-8 text-xs rounded-sm" /></div>
              <div className="space-y-1"><Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">CTA Secundario - Enlace</Label>
                <Input value={form.cta_secondary_link||""} onChange={e=>setForm(p=>({...p,cta_secondary_link:e.target.value}))} placeholder="/vehiculos" className="h-8 text-xs rounded-sm" /></div>
            </div>
            <p className="text-[9px] text-slate-400">Deja los campos de texto y CTA vacios si solo quieres mostrar la imagen.</p>

            {/* Iter 214: modo de ajuste de imagen */}
            <div className="space-y-1.5 p-2.5 border border-slate-200 rounded-sm bg-slate-50/50">
              <Label htmlFor="slide-image-fit" className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Ajuste de imagen</Label>
              <select
                id="slide-image-fit"
                value={form.image_fit || "cover"}
                onChange={(e) => setForm(p => ({ ...p, image_fit: e.target.value }))}
                className="w-full h-8 text-xs rounded-sm border border-slate-300 bg-white px-2"
                data-testid="slide-image-fit"
              >
                <option value="cover">Cubrir todo — puede recortar (ideal para fotos)</option>
                <option value="contain">Mostrar completa — sin recortar (ideal para carteles, logos, horarios)</option>
                <option value="auto">Automático (cubrir si overlay activo, mostrar completa si no)</option>
              </select>
              <p className="text-[10px] text-slate-500 leading-tight">
                Usa <strong>Mostrar completa</strong> para slides con texto, logos u horarios. Usa <strong>Cubrir todo</strong> para fotografías. Con <strong>Mostrar completa</strong>, los espacios libres se rellenan con fondo oscuro.
              </p>
            </div>

            {/* Iter 211: toggle explícito "Mostrar overlay" — desactivado = imagen limpia sin texto/CTAs */}
            <div className="flex items-start gap-2 p-2.5 border border-slate-200 rounded-sm bg-slate-50/50">
              <Switch
                checked={form.overlay_enabled !== false}
                onCheckedChange={v => setForm(p => ({ ...p, overlay_enabled: v }))}
                data-testid="slide-overlay-enabled"
              />
              <div className="flex-1">
                <Label className="text-xs text-slate-700 font-semibold">Mostrar overlay (texto y CTAs)</Label>
                <p className="text-[10px] text-slate-500 leading-tight mt-0.5">
                  Si desactivas este interruptor, el slide mostrará solo la imagen (sin badge, título, subtítulo ni botones), aunque esos campos tengan contenido.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Color texto</Label>
                <Select value={form.text_color||"white"} onValueChange={v=>setForm(p=>({...p,text_color:v}))}>
                  <SelectTrigger className="h-8 text-xs rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="white">Claro</SelectItem><SelectItem value="dark">Oscuro</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Opacidad overlay</Label>
                <Input type="number" min={0} max={100} value={form.overlay_opacity??70} onChange={e=>setForm(p=>({...p,overlay_opacity:parseInt(e.target.value)||0}))} className="h-8 text-xs rounded-sm" /></div>
              <div className="space-y-1"><Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Orden</Label>
                <Input type="number" value={form.order??0} onChange={e=>setForm(p=>({...p,order:parseInt(e.target.value)||0}))} className="h-8 text-xs rounded-sm" /></div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.active??true} onCheckedChange={v=>setForm(p=>({...p,active:v}))} data-testid="slide-active" />
              <Label className="text-xs text-slate-600">Slide activo (visible en la home)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setEditId(null)} className="rounded-sm text-xs uppercase h-8">Cancelar</Button>
            <Button onClick={saveSlide} disabled={uploading} className="bg-slate-900 hover:bg-slate-800 rounded-sm text-xs uppercase font-semibold h-8" data-testid="save-slide-btn">Guardar Slide</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ═══════════════════════════════════════════
   REUSABLE: ItemEditor
   ═══════════════════════════════════════════ */
function ItemEditor({ items, setItems, fields, title, icon: Icon, compact }) {
  const [editIdx, setEditIdx] = useState(null);
  const [form, setForm] = useState({});
  const openEdit = (i) => { setEditIdx(i); setForm({ ...items[i] }); };
  const openNew = () => { setEditIdx(-1); setForm(fields.reduce((a, f) => ({ ...a, [f.key]: f.default ?? "" }), { order: items.length + 1 })); };
  const save = () => { if (editIdx === -1) setItems([...items, form]); else setItems(items.map((it, i) => i === editIdx ? form : it)); setEditIdx(null); };
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));
  const moveUp = (i) => { if (!i) return; const a=[...items]; [a[i-1],a[i]]=[a[i],a[i-1]]; setItems(a.map((x,idx)=>({...x,order:idx+1}))); };
  const moveDown = (i) => { if (i>=items.length-1) return; const a=[...items]; [a[i],a[i+1]]=[a[i+1],a[i]]; setItems(a.map((x,idx)=>({...x,order:idx+1}))); };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">{Icon && <Icon className="w-3.5 h-3.5 text-red-600" />}{title}</h4>
        <Button variant="outline" size="sm" onClick={openNew} className="rounded-sm text-[10px] h-6 px-2 uppercase tracking-wider" data-testid={`add-${title.toLowerCase().replace(/\s/g,"-")}`}><Plus className="w-3 h-3 mr-1" /> Nuevo</Button>
      </div>
      <div className={compact?"space-y-1":"space-y-1.5"}>
        {items.map((it,i)=>(
          <div key={i} className="flex items-center gap-1.5 p-2 border border-slate-200 rounded-sm bg-white text-xs group">
            <GripVertical className="w-3 h-3 text-slate-300 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="font-semibold text-slate-900 truncate block">{it.title||it.name||it.value||it.label||it.alt||`#${i+1}`}</span>
              {!compact && <span className="text-slate-400 truncate block text-[10px]">{it.description||it.text||it.label||it.location||it.image_url||""}</span>}
            </div>
            <div className="flex items-center gap-0 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button variant="ghost" size="sm" onClick={()=>moveUp(i)} disabled={!i} className="h-6 w-6 p-0"><ChevronUp className="w-3 h-3" /></Button>
              <Button variant="ghost" size="sm" onClick={()=>moveDown(i)} disabled={i>=items.length-1} className="h-6 w-6 p-0"><ChevronDown className="w-3 h-3" /></Button>
              <Button variant="ghost" size="sm" onClick={()=>openEdit(i)} className="h-6 w-6 p-0"><Pencil className="w-3 h-3" /></Button>
              <Button variant="ghost" size="sm" onClick={()=>remove(i)} className="h-6 w-6 p-0 text-red-500"><Trash2 className="w-3 h-3" /></Button>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={editIdx!==null} onOpenChange={()=>setEditIdx(null)}>
        <DialogContent className="max-w-md rounded-sm max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="heading-industrial text-sm uppercase">{editIdx===-1?"Nuevo":"Editar"} {title}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            {fields.map(f=>(
              <div key={f.key} className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{f.label}</Label>
                {f.type==="textarea" ? <Textarea value={form[f.key]||""} onChange={e=>setForm(p=>({...p,[f.key]:e.target.value}))} className="rounded-sm border-slate-200 text-sm" rows={3} />
                : f.type==="number" ? <Input type="number" value={form[f.key]??0} onChange={e=>setForm(p=>({...p,[f.key]:parseInt(e.target.value)||0}))} className="rounded-sm h-8 border-slate-200 text-sm" />
                : f.type==="switch" ? <Switch checked={!!form[f.key]} onCheckedChange={v=>setForm(p=>({...p,[f.key]:v}))} />
                : <Input value={form[f.key]||""} onChange={e=>setForm(p=>({...p,[f.key]:e.target.value}))} className="rounded-sm h-8 border-slate-200 text-sm" placeholder={f.placeholder||""} />}
                {f.hint && <p className="text-[9px] text-slate-400">{f.hint}</p>}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setEditIdx(null)} className="rounded-sm text-xs uppercase h-8">Cancelar</Button>
            <Button onClick={save} className="bg-slate-900 hover:bg-slate-800 rounded-sm text-xs uppercase font-semibold h-8">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ═══════════════════════════════════════════
   REUSABLE: SField
   ═══════════════════════════════════════════ */
function SField({label,value,onChange,type="text",hint,testid,rows}) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</Label>
      {type==="textarea" ? <Textarea value={value||""} onChange={e=>onChange(e.target.value)} className="rounded-sm border-slate-200 text-sm" rows={rows||2} data-testid={testid} />
      : <Input value={value||""} onChange={e=>onChange(e.target.value)} className="rounded-sm h-8 border-slate-200 text-sm" data-testid={testid} />}
      {hint && <p className="text-[9px] text-slate-400">{hint}</p>}
    </div>
  );
}

/* ═══════════════════════════════════════════
   GALLERY EDITOR (URL + Upload)
   ═══════════════════════════════════════════ */
function GalleryEditor({ items, setItems }) {
  const [editIdx, setEditIdx] = useState(null);
  const [form, setForm] = useState({ image_url: "", alt: "", order: 0, source: "url" });
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState("");

  const openNew = () => { setEditIdx(-1); setForm({ image_url: "", alt: "", order: items.length + 1, source: "url" }); setPreview(""); };
  const openEdit = (i) => {
    const it = items[i];
    const isUpload = (it.image_url || "").startsWith("/api/branding/gallery/");
    setEditIdx(i);
    setForm({ ...it, source: isUpload ? "upload" : "url" });
    setPreview(it.image_url || "");
  };
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));
  const moveUp = (i) => { if (!i) return; const a=[...items]; [a[i-1],a[i]]=[a[i],a[i-1]]; setItems(a.map((x,idx)=>({...x,order:idx+1}))); };
  const moveDown = (i) => { if (i>=items.length-1) return; const a=[...items]; [a[i],a[i+1]]=[a[i+1],a[i]]; setItems(a.map((x,idx)=>({...x,order:idx+1}))); };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["jpg","jpeg","png","webp"].includes(ext)) { toast.error("Formato no permitido. Usa JPG, PNG o WebP."); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Imagen demasiado grande. Maximo 10MB."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/branding/upload-gallery`, fd, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } });
      const url = res.data.url;
      setForm(p => ({ ...p, image_url: url }));
      setPreview(url);
      toast.success("Imagen subida correctamente");
    } catch (err) {
      toast.error("Error al subir la imagen");
    }
    setUploading(false);
  };

  const save = () => {
    if (!form.image_url) { toast.error("Debes indicar una URL o subir una imagen"); return; }
    const clean = { image_url: form.image_url, alt: form.alt || "", order: form.order || 0 };
    if (editIdx === -1) setItems([...items, clean]);
    else setItems(items.map((it, i) => i === editIdx ? clean : it));
    setEditIdx(null);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold text-slate-700 flex items-center gap-1.5"><ImagePlus className="w-3.5 h-3.5 text-red-600" />Galeria de fotos</h4>
        <Button variant="outline" size="sm" onClick={openNew} className="rounded-sm text-[10px] h-6 px-2 uppercase tracking-wider" data-testid="add-gallery-image"><Plus className="w-3 h-3 mr-1" /> Nueva</Button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {items.map((it, i) => (
          <div key={i} className="relative group border border-slate-200 rounded-sm overflow-hidden bg-slate-50 aspect-[4/3]" data-testid={`gallery-item-${i}`}>
            <img src={getImageUrl(it.image_url)} alt={it.alt || ""} className="w-full h-full object-cover" onError={(e) => { e.target.style.display="none"; }} />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
              <Button variant="ghost" size="sm" onClick={() => moveUp(i)} disabled={!i} className="h-7 w-7 p-0 bg-white/90 hover:bg-white"><ChevronUp className="w-3.5 h-3.5" /></Button>
              <Button variant="ghost" size="sm" onClick={() => moveDown(i)} disabled={i>=items.length-1} className="h-7 w-7 p-0 bg-white/90 hover:bg-white"><ChevronDown className="w-3.5 h-3.5" /></Button>
              <Button variant="ghost" size="sm" onClick={() => openEdit(i)} className="h-7 w-7 p-0 bg-white/90 hover:bg-white"><Pencil className="w-3.5 h-3.5" /></Button>
              <Button variant="ghost" size="sm" onClick={() => remove(i)} className="h-7 w-7 p-0 bg-white/90 hover:bg-white text-red-500"><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
            {it.alt && <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] px-1.5 py-0.5 truncate">{it.alt}</div>}
          </div>
        ))}
      </div>
      <Dialog open={editIdx !== null} onOpenChange={() => setEditIdx(null)}>
        <DialogContent className="max-w-lg rounded-sm max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="heading-industrial text-sm uppercase">{editIdx === -1 ? "Nueva imagen" : "Editar imagen"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Tipo de imagen</Label>
              <div className="flex gap-2">
                <Button variant={form.source === "url" ? "default" : "outline"} size="sm" onClick={() => setForm(p => ({ ...p, source: "url" }))}
                  className={`rounded-sm text-xs flex-1 ${form.source === "url" ? "bg-slate-900 text-white" : ""}`} data-testid="gallery-source-url">
                  <Link2 className="w-3.5 h-3.5 mr-1.5" /> URL externa
                </Button>
                <Button variant={form.source === "upload" ? "default" : "outline"} size="sm" onClick={() => setForm(p => ({ ...p, source: "upload" }))}
                  className={`rounded-sm text-xs flex-1 ${form.source === "upload" ? "bg-slate-900 text-white" : ""}`} data-testid="gallery-source-upload">
                  <Upload className="w-3.5 h-3.5 mr-1.5" /> Subir imagen
                </Button>
              </div>
            </div>

            {form.source === "url" && (
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">URL de la imagen</Label>
                <Input value={form.image_url || ""} onChange={e => { setForm(p => ({ ...p, image_url: e.target.value })); setPreview(e.target.value); }}
                  className="rounded-sm h-8 border-slate-200 text-sm" placeholder="https://..." data-testid="gallery-url-input" />
              </div>
            )}

            {form.source === "upload" && (
              <div className="space-y-2">
                <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Subir archivo</Label>
                <div className="border-2 border-dashed border-slate-300 rounded-sm p-4 text-center hover:border-slate-400 transition-colors">
                  <input type="file" accept=".jpg,.jpeg,.png,.webp" onChange={handleUpload} className="hidden" id="gallery-upload-input" data-testid="gallery-file-input" />
                  <label htmlFor="gallery-upload-input" className="cursor-pointer flex flex-col items-center gap-2">
                    <Upload className="w-6 h-6 text-slate-400" />
                    <span className="text-xs text-slate-500">{uploading ? "Subiendo..." : "Haz clic o arrastra una imagen"}</span>
                    <span className="text-[9px] text-slate-400">JPG, PNG o WebP. Max 10MB</span>
                  </label>
                </div>
                {form.image_url && form.image_url.startsWith("/api") && (
                  <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 p-2 rounded-sm">
                    <BadgeCheck className="w-3.5 h-3.5" /> Imagen subida correctamente
                    <Button variant="ghost" size="sm" className="ml-auto h-6 px-2 text-red-500 text-[10px]" onClick={() => { setForm(p => ({ ...p, image_url: "" })); setPreview(""); }}>
                      <Trash2 className="w-3 h-3 mr-1" /> Eliminar
                    </Button>
                  </div>
                )}
              </div>
            )}

            {preview && (
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Preview</Label>
                <div className="border border-slate-200 rounded-sm overflow-hidden bg-slate-50 aspect-video">
                  <img src={getImageUrl(preview)} alt="preview"
                    className="w-full h-full object-cover" onError={(e) => { e.target.style.display="none"; }} />
                </div>
              </div>
            )}

            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Texto alternativo (descripcion de la foto)</Label>
              <Input value={form.alt || ""} onChange={e => setForm(p => ({ ...p, alt: e.target.value }))} className="rounded-sm h-8 border-slate-200 text-sm" placeholder="Ej: Taller de mecanica" data-testid="gallery-alt-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Orden</Label>
              <Input type="number" value={form.order ?? 0} onChange={e => setForm(p => ({ ...p, order: parseInt(e.target.value) || 0 }))} className="rounded-sm h-8 border-slate-200 text-sm w-24" data-testid="gallery-order-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditIdx(null)} className="rounded-sm text-xs uppercase h-8">Cancelar</Button>
            <Button onClick={save} className="bg-slate-900 hover:bg-slate-800 rounded-sm text-xs uppercase font-semibold h-8" data-testid="gallery-save-btn" disabled={uploading}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ═══════════════════════════════════════════
   SERVICE PAGE EDITOR
   ═══════════════════════════════════════════ */
function ServicePageEditor({slug,label,icon:Icon,content,setContent}) {
  const sp = content.service_pages?.[slug] || {};
  const sv = sp.sections_visibility || {};
  const up = (key,val) => setContent(p=>({...p,service_pages:{...p.service_pages,[slug]:{...sp,[key]:val}}}));
  const toggleSv = (key,val) => up("sections_visibility", {...sv, [key]: val});

  return (
    <Card className="border-slate-200 rounded-sm">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><Icon className="w-4 h-4 text-red-600" /> {label}</CardTitle>
          <div className="flex items-center gap-2">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Activa</Label>
            <Switch checked={sp.active!==false} onCheckedChange={v=>up("active",v)} />
          </div>
        </div>
        <CardDescription className="text-xs">/{slug==="chapa"?"chapa-y-pintura":slug}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Textos principales */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <SField label="Titulo" value={sp.title} onChange={v=>up("title",v)} />
          <SField label="Subtitulo" value={sp.subtitle} onChange={v=>up("subtitle",v)} />
        </div>
        <SField label="Descripcion" value={sp.description} onChange={v=>up("description",v)} type="textarea" rows={3} />
        <ImageUploadField label="Imagen hero de la pagina" value={sp.hero_image} onChange={v=>up("hero_image",v)} hint="Imagen de fondo de la cabecera de la pagina" />

        {/* Galeria */}
        <div className="pt-3 border-t border-slate-100">
          <GalleryEditor
            items={sp.gallery||[]}
            setItems={v=>up("gallery",v)}
          />
        </div>

        {/* Servicios */}
        <div className="pt-3 border-t border-slate-100">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <SField label="Titulo seccion servicios" value={sp.services_title} onChange={v=>up("services_title",v)} />
            <SField label="Subtitulo servicios" value={sp.services_subtitle} onChange={v=>up("services_subtitle",v)} />
          </div>
          <IconItemEditor
            items={sp.services||[]}
            setItems={v=>up("services",v)}
            title="Tarjetas de servicios"
            icon={Wrench}
            fields={[
              {key:"title",label:"Titulo",type:"text"},
              {key:"description",label:"Descripcion",type:"textarea"},
              {key:"icon",label:"Icono",type:"text"},
            ]}
          />
        </div>

        {/* Ventajas */}
        <div className="pt-3 border-t border-slate-100">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <SField label="Titulo seccion ventajas" value={sp.advantages_title} onChange={v=>up("advantages_title",v)} />
            <SField label="Subtitulo ventajas" value={sp.advantages_subtitle} onChange={v=>up("advantages_subtitle",v)} />
          </div>
          <ItemEditor
            items={sp.advantages||[]}
            setItems={v=>up("advantages",v)}
            title="Ventajas"
            icon={Award}
            compact
            fields={[
              {key:"title",label:"Titulo",type:"text"},
              {key:"description",label:"Descripcion",type:"text"},
            ]}
          />
        </div>

        {/* CTA */}
        <div className="pt-3 border-t border-slate-100">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <SField label="Titulo CTA final" value={sp.cta_title} onChange={v=>up("cta_title",v)} />
            <SField label="Texto CTA final" value={sp.cta_text} onChange={v=>up("cta_text",v)} />
          </div>
        </div>

        {/* Visibilidad bloques */}
        <div className="pt-3 border-t border-slate-100">
          <h4 className="text-xs font-semibold text-slate-700 flex items-center gap-1.5 mb-3"><Eye className="w-3.5 h-3.5 text-red-600" />Visibilidad de bloques</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {[{k:"description",l:"Descripcion"},{k:"gallery",l:"Galeria"},{k:"services",l:"Servicios"},{k:"advantages",l:"Ventajas"},{k:"cta",l:"CTA final"}].map(s=>(
              <div key={s.k} className="flex items-center justify-between p-2 border border-slate-200 rounded-sm">
                <span className="text-[10px] font-medium text-slate-700">{s.l}</span>
                <Switch checked={sv[s.k]!==false} onCheckedChange={v=>toggleSv(s.k,v)} />
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ═══════════════════════════════════════════
   LOGO MANAGER
   ═══════════════════════════════════════════ */
function LogoManager() {
  const { axiosAuth } = useAuth();
  const [branding, setBranding] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { axios.get(`${API}/branding`).then(r=>setBranding(r.data)).catch(()=>{}); }, []);

  const uploadLogo = async (file, variant) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await axiosAuth().post(`/branding/logo?variant=${variant}`, fd, {headers:{"Content-Type":"multipart/form-data"}});
      setBranding(p=>({...p,[variant==="main"?"logo_url":"logo_dark_url"]:res.data.url}));
      toast.success("Logo subido correctamente");
    } catch { toast.error("Error al subir logo"); }
    finally { setUploading(false); }
  };

  const saveBranding = async () => {
    try { await axiosAuth().put("/branding", branding); toast.success("Identidad visual guardada"); }
    catch { toast.error("Error al guardar"); }
  };

  if (!branding) return null;
  const baseUrl = API.replace("/api","");

  return (
    <Card className="border-slate-200 rounded-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><ImageIcon className="w-4 h-4 text-red-600" /> Identidad Visual</CardTitle>
        <CardDescription className="text-xs">Logo, texto de marca y elementos visuales de la cabecera publica</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Logo principal</Label>
            <div className="border border-dashed border-slate-300 rounded-sm p-4 bg-slate-50 flex flex-col items-center gap-3">
              {branding.logo_url ? <img src={`${getImageUrl(branding.logo_url)}?t=${Date.now()}`} alt="Logo" className="h-12 w-auto object-contain" data-testid="preview-logo-main" />
              : <div className="h-12 flex items-center justify-center text-slate-400 text-xs">Sin logo</div>}
              <label className="cursor-pointer">
                <input type="file" accept="image/*" className="hidden" onChange={e=>e.target.files[0]&&uploadLogo(e.target.files[0],"main")} />
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-red-600 hover:text-red-700"><Upload className="w-3 h-3" />{uploading?"Subiendo...":"Subir logo"}</span>
              </label>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Logo version oscura (opcional)</Label>
            <div className="border border-dashed border-slate-300 rounded-sm p-4 bg-slate-900 flex flex-col items-center gap-3">
              {branding.logo_dark_url ? <img src={`${getImageUrl(branding.logo_dark_url)}?t=${Date.now()}`} alt="Logo dark" className="h-12 w-auto object-contain" data-testid="preview-logo-dark" />
              : branding.logo_url ? <img src={`${getImageUrl(branding.logo_url)}?t=${Date.now()}`} alt="Logo" className="h-12 w-auto object-contain brightness-0 invert" />
              : <div className="h-12 flex items-center justify-center text-slate-500 text-xs">Sin logo</div>}
              <label className="cursor-pointer">
                <input type="file" accept="image/*" className="hidden" onChange={e=>e.target.files[0]&&uploadLogo(e.target.files[0],"dark")} />
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-red-400 hover:text-red-300"><Upload className="w-3 h-3" />Subir version oscura</span>
              </label>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <SField label="Texto auxiliar del logo" value={branding.logo_text} onChange={v=>setBranding(p=>({...p,logo_text:v}))} hint="Visible junto al logo (ej: Servicio Oficial Stellantis)" />
          <SField label="Tagline / Eslogan" value={branding.tagline} onChange={v=>setBranding(p=>({...p,tagline:v}))} hint="Breve texto debajo del nombre" />
        </div>
        <Button onClick={saveBranding} className="bg-slate-900 hover:bg-slate-800 rounded-sm text-xs uppercase tracking-wider font-semibold h-8 px-4" data-testid="save-branding"><Save className="w-3.5 h-3.5 mr-1.5" />Guardar identidad</Button>
      </CardContent>
    </Card>
  );
}


/* ═══════════════════════════════════════════
   CONOCENOS EDITOR
   ═══════════════════════════════════════════ */
function ConocenosEditor({ content, setContent }) {
  const cn = content.conocenos || {};
  const up = (section, key, val) => {
    setContent(p => ({
      ...p,
      conocenos: { ...p.conocenos, [section]: { ...(p.conocenos || {})[section], [key]: val } }
    }));
  };
  const upRoot = (key, val) => {
    setContent(p => ({ ...p, conocenos: { ...p.conocenos, [key]: val } }));
  };

  const homeBlock = content.conocenos_home_block || {};
  const upHome = (key, val) => {
    setContent(p => ({ ...p, conocenos_home_block: { ...p.conocenos_home_block, [key]: val } }));
  };

  return (
    <Tabs defaultValue="hero-cn" className="space-y-3">
      <TabsList className="bg-slate-100 rounded-sm h-auto flex-wrap gap-0.5 p-1">
        {[
          { v: "hero-cn", l: "Hero", i: PanelTop },
          { v: "quienes", l: "Quienes somos", i: Users },
          { v: "trayectoria", l: "Trayectoria", i: BarChart3 },
          { v: "que-hacemos", l: "Que hacemos", i: Wrench },
          { v: "forma", l: "Forma de trabajar", i: Zap },
          { v: "centros-cn", l: "Centros", i: Building2 },
          { v: "confianza", l: "Confianza", i: Award },
          { v: "cta-cn", l: "CTA final", i: MessageSquare },
          { v: "seo-cn", l: "SEO", i: Eye },
          { v: "home-block", l: "Bloque Home", i: Monitor },
        ].map(t => (
          <TabsTrigger key={t.v} value={t.v} className="rounded-sm text-[9px] uppercase tracking-wider font-semibold data-[state=active]:bg-white px-2.5 py-1" data-testid={`cn-tab-${t.v}`}>
            <t.i className="w-3 h-3 mr-1" />{t.l}
          </TabsTrigger>
        ))}
      </TabsList>

      {/* HERO */}
      <TabsContent value="hero-cn">
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><PanelTop className="w-4 h-4 text-red-600" /> Hero de Conocenos</CardTitle>
            <CardDescription className="text-xs">Cabecera visual de la pagina /conocenos</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <SField label="Etiqueta superior" value={(cn.hero || {}).label} onChange={v => up("hero", "label", v)} hint="Vacio = Sobre nosotros" />
            <SField label="Titulo (H1)" value={(cn.hero || {}).title} onChange={v => up("hero", "title", v)} hint="Vacio = Conocenos" />
            <SField label="Subtitulo" value={(cn.hero || {}).subtitle} onChange={v => up("hero", "subtitle", v)} type="textarea" />
            <ImageUploadField label="Imagen de fondo" value={(cn.hero || {}).image_url} onChange={v => up("hero", "image_url", v)} />
          </CardContent>
        </Card>
      </TabsContent>

      {/* QUIENES SOMOS */}
      <TabsContent value="quienes">
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><Users className="w-4 h-4 text-red-600" /> Quienes somos</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <SField label="Etiqueta" value={(cn.quienes_somos || {}).label} onChange={v => up("quienes_somos", "label", v)} hint="Vacio = Nuestra historia" />
            <SField label="Titulo" value={(cn.quienes_somos || {}).title} onChange={v => up("quienes_somos", "title", v)} hint="Vacio = Quienes somos" />
            <SField label="Texto descriptivo" value={(cn.quienes_somos || {}).text} onChange={v => up("quienes_somos", "text", v)} type="textarea" rows={5} hint="Puedes usar saltos de linea para crear parrafos" />
            <ImageUploadField label="Imagen lateral" value={(cn.quienes_somos || {}).image_url} onChange={v => up("quienes_somos", "image_url", v)} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <SField label="Cifra destacada" value={(cn.quienes_somos || {}).stat_value} onChange={v => up("quienes_somos", "stat_value", v)} hint="Ej: 40+" />
              <SField label="Etiqueta de la cifra" value={(cn.quienes_somos || {}).stat_label} onChange={v => up("quienes_somos", "stat_label", v)} hint="Ej: años de experiencia" />
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      {/* TRAYECTORIA */}
      <TabsContent value="trayectoria">
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><BarChart3 className="w-4 h-4 text-red-600" /> Trayectoria</CardTitle>
            <CardDescription className="text-xs">Timeline con hitos de la empresa</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <SField label="Etiqueta" value={(cn.trayectoria || {}).label} onChange={v => up("trayectoria", "label", v)} hint="Vacio = Nuestra trayectoria" />
            <SField label="Titulo" value={(cn.trayectoria || {}).title} onChange={v => up("trayectoria", "title", v)} hint="Vacio = Trayectoria" />
            <SField label="Subtitulo" value={(cn.trayectoria || {}).subtitle} onChange={v => up("trayectoria", "subtitle", v)} />
            <ItemEditor
              items={(cn.trayectoria || {}).items || []}
              setItems={v => upRoot("trayectoria", { ...(cn.trayectoria || {}), items: v })}
              title="Hitos"
              icon={BarChart3}
              fields={[
                { key: "year", label: "Ano", type: "text", placeholder: "Ej: 1985" },
                { key: "title", label: "Titulo del hito", type: "text" },
                { key: "description", label: "Descripcion", type: "textarea" },
              ]}
            />
          </CardContent>
        </Card>
      </TabsContent>

      {/* QUE HACEMOS */}
      <TabsContent value="que-hacemos">
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><Wrench className="w-4 h-4 text-red-600" /> Que hacemos</CardTitle>
            <CardDescription className="text-xs">Servicios principales de la empresa</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <SField label="Etiqueta" value={(cn.que_hacemos || {}).label} onChange={v => up("que_hacemos", "label", v)} hint="Vacio = Nuestros servicios" />
            <SField label="Titulo" value={(cn.que_hacemos || {}).title} onChange={v => up("que_hacemos", "title", v)} hint="Vacio = Que hacemos" />
            <SField label="Subtitulo" value={(cn.que_hacemos || {}).subtitle} onChange={v => up("que_hacemos", "subtitle", v)} />
            <IconItemEditor
              items={(cn.que_hacemos || {}).items || []}
              setItems={v => upRoot("que_hacemos", { ...(cn.que_hacemos || {}), items: v })}
              title="Servicios"
              icon={Wrench}
              fields={[
                { key: "title", label: "Titulo", type: "text" },
                { key: "description", label: "Descripcion", type: "textarea" },
                { key: "icon", label: "Icono", type: "text" },
              ]}
            />
          </CardContent>
        </Card>
      </TabsContent>

      {/* FORMA DE TRABAJAR */}
      <TabsContent value="forma">
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><Zap className="w-4 h-4 text-red-600" /> Forma de trabajar</CardTitle>
            <CardDescription className="text-xs">Pasos del proceso de trabajo</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <SField label="Etiqueta" value={(cn.forma_trabajar || {}).label} onChange={v => up("forma_trabajar", "label", v)} hint="Vacio = Nuestro proceso" />
            <SField label="Titulo" value={(cn.forma_trabajar || {}).title} onChange={v => up("forma_trabajar", "title", v)} hint="Vacio = Nuestra forma de trabajar" />
            <SField label="Subtitulo" value={(cn.forma_trabajar || {}).subtitle} onChange={v => up("forma_trabajar", "subtitle", v)} />
            <ItemEditor
              items={(cn.forma_trabajar || {}).items || []}
              setItems={v => upRoot("forma_trabajar", { ...(cn.forma_trabajar || {}), items: v })}
              title="Pasos"
              icon={Zap}
              fields={[
                { key: "step", label: "Numero del paso", type: "text", placeholder: "1" },
                { key: "title", label: "Titulo", type: "text" },
                { key: "description", label: "Descripcion", type: "textarea" },
              ]}
            />
          </CardContent>
        </Card>
      </TabsContent>

      {/* CENTROS */}
      <TabsContent value="centros-cn">
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><Building2 className="w-4 h-4 text-red-600" /> Nuestros centros</CardTitle>
            <CardDescription className="text-xs">Textos de la seccion de centros en la pagina Conocenos. Los centros se gestionan en Configuracion.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <SField label="Etiqueta" value={(cn.centros || {}).label} onChange={v => up("centros", "label", v)} hint="Vacio = Donde estamos" />
            <SField label="Titulo" value={(cn.centros || {}).title} onChange={v => up("centros", "title", v)} hint="Vacio = Nuestros centros" />
            <SField label="Subtitulo" value={(cn.centros || {}).subtitle} onChange={v => up("centros", "subtitle", v)} />
            <p className="text-[9px] text-slate-400 bg-slate-50 p-2 rounded-sm">Los centros se configuran en <strong>Configuracion &gt; Centros</strong>. Aqui solo se configuran los textos.</p>
          </CardContent>
        </Card>
      </TabsContent>

      {/* CONFIANZA */}
      <TabsContent value="confianza">
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><Award className="w-4 h-4 text-red-600" /> Por que confiar en nosotros</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <SField label="Etiqueta" value={(cn.confianza || {}).label} onChange={v => up("confianza", "label", v)} hint="Vacio = Garantia de calidad" />
            <SField label="Titulo" value={(cn.confianza || {}).title} onChange={v => up("confianza", "title", v)} hint="Vacio = Por que confiar en ASCAUTO" />
            <SField label="Subtitulo" value={(cn.confianza || {}).subtitle} onChange={v => up("confianza", "subtitle", v)} />
            <IconItemEditor
              items={(cn.confianza || {}).items || []}
              setItems={v => upRoot("confianza", { ...(cn.confianza || {}), items: v })}
              title="Razones de confianza"
              icon={Award}
              fields={[
                { key: "title", label: "Titulo", type: "text" },
                { key: "description", label: "Descripcion", type: "textarea" },
                { key: "icon", label: "Icono", type: "text" },
              ]}
            />
          </CardContent>
        </Card>
      </TabsContent>

      {/* CTA FINAL */}
      <TabsContent value="cta-cn">
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><MessageSquare className="w-4 h-4 text-red-600" /> CTA Final de Conocenos</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <SField label="Titulo" value={(cn.cta_final || {}).title} onChange={v => up("cta_final", "title", v)} hint="Vacio = Ven a conocernos" />
            <SField label="Subtitulo" value={(cn.cta_final || {}).subtitle} onChange={v => up("cta_final", "subtitle", v)} type="textarea" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <SField label="Texto del boton" value={(cn.cta_final || {}).button_text} onChange={v => up("cta_final", "button_text", v)} hint="Vacio = Reservar Cita" />
              <SField label="Enlace del boton" value={(cn.cta_final || {}).button_link} onChange={v => up("cta_final", "button_link", v)} hint="Vacio = /cita" />
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      {/* SEO */}
      <TabsContent value="seo-cn">
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><Eye className="w-4 h-4 text-red-600" /> SEO de Conocenos</CardTitle>
            <CardDescription className="text-xs">Etiquetas title y meta description para el posicionamiento en buscadores.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <SField label="Title (etiqueta title)" value={(cn.seo || {}).title} onChange={v => up("seo", "title", v)} hint="Vacio = Conocenos | ASCAUTO" />
            <SField label="Meta description" value={(cn.seo || {}).description} onChange={v => up("seo", "description", v)} type="textarea" hint="Descripcion breve (150-160 caracteres recomendados)" />
          </CardContent>
        </Card>
      </TabsContent>

      {/* BLOQUE HOME */}
      <TabsContent value="home-block">
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><Monitor className="w-4 h-4 text-red-600" /> Bloque de Conocenos en la Home</CardTitle>
            <CardDescription className="text-xs">Resumen que aparece en la pagina principal con enlace a /conocenos. Su visibilidad se controla en "Visibilidad y orden".</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <SField label="Etiqueta" value={homeBlock.label} onChange={v => upHome("label", v)} hint="Vacio = Sobre nosotros" />
            <SField label="Titulo" value={homeBlock.title} onChange={v => upHome("title", v)} hint="Vacio = Conoce ASCAUTO" />
            <SField label="Texto descriptivo" value={homeBlock.text} onChange={v => upHome("text", v)} type="textarea" />
            <SField label="Texto del boton" value={homeBlock.button_text} onChange={v => upHome("button_text", v)} hint="Vacio = Conocenos" />
            <ImageUploadField label="Imagen lateral" value={homeBlock.image_url} onChange={v => upHome("image_url", v)} />
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
}



/* ═══════════════════════════════════════════
   CONTACTO EDITOR
   ═══════════════════════════════════════════ */
function ContactoEditor({ content, setContent }) {
  const cn = content.contacto || {};
  const up = (section, key, val) => {
    setContent(p => ({
      ...p,
      contacto: { ...p.contacto, [section]: { ...(p.contacto || {})[section], [key]: val } }
    }));
  };
  const upRoot = (key, val) => {
    setContent(p => ({ ...p, contacto: { ...p.contacto, [key]: val } }));
  };

  return (
    <Tabs defaultValue="hero-ct" className="space-y-3">
      <TabsList className="bg-slate-100 rounded-sm h-auto flex-wrap gap-0.5 p-1">
        {[
          { v: "hero-ct", l: "Hero", i: PanelTop },
          { v: "form-ct", l: "Formulario", i: MessageSquare },
          { v: "channels-ct", l: "Canales", i: Phone },
          { v: "centros-ct2", l: "Centros", i: Building2 },
          { v: "cta-ct", l: "CTA final", i: MessageSquare },
          { v: "config-ct", l: "Configuracion", i: Mail },
          { v: "seo-ct", l: "SEO", i: Eye },
        ].map(t => (
          <TabsTrigger key={t.v} value={t.v} className="rounded-sm text-[9px] uppercase tracking-wider font-semibold data-[state=active]:bg-white px-2.5 py-1">
            <t.i className="w-3 h-3 mr-1" />{t.l}
          </TabsTrigger>
        ))}
      </TabsList>

      <TabsContent value="hero-ct">
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-3"><CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><PanelTop className="w-4 h-4 text-red-600" /> Hero de Contacto</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <SField label="Etiqueta" value={(cn.hero || {}).label} onChange={v => up("hero", "label", v)} hint="Vacio = Contacto" />
            <SField label="Titulo (H1)" value={(cn.hero || {}).title} onChange={v => up("hero", "title", v)} hint="Vacio = Contacto" />
            <SField label="Subtitulo" value={(cn.hero || {}).subtitle} onChange={v => up("hero", "subtitle", v)} type="textarea" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <SField label="CTA primario" value={(cn.hero || {}).cta_primary} onChange={v => up("hero", "cta_primary", v)} hint="Vacio = Llamar ahora" />
              <SField label="CTA secundario" value={(cn.hero || {}).cta_secondary} onChange={v => up("hero", "cta_secondary", v)} hint="Vacio = Reservar cita" />
            </div>
            <ImageUploadField label="Imagen de fondo" value={(cn.hero || {}).image_url} onChange={v => up("hero", "image_url", v)} />
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="form-ct">
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-3"><CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><MessageSquare className="w-4 h-4 text-red-600" /> Formulario de contacto</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <SField label="Etiqueta" value={(cn.form || {}).label} onChange={v => up("form", "label", v)} hint="Vacio = Formulario" />
            <SField label="Titulo" value={(cn.form || {}).title} onChange={v => up("form", "title", v)} hint="Vacio = Envianos tu consulta" />
            <SField label="Subtitulo" value={(cn.form || {}).subtitle} onChange={v => up("form", "subtitle", v)} />
            <SField label="Texto de exito" value={(cn.form || {}).success_text} onChange={v => up("form", "success_text", v)} hint="Texto que aparece tras enviar correctamente" />
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="channels-ct">
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-3"><CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><Phone className="w-4 h-4 text-red-600" /> Canales directos</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <SField label="Etiqueta" value={(cn.channels || {}).label} onChange={v => up("channels", "label", v)} hint="Vacio = Contacto directo" />
            <SField label="Titulo" value={(cn.channels || {}).title} onChange={v => up("channels", "title", v)} hint="Vacio = Tambien puedes contactar directamente" />
            <SField label="Subtitulo" value={(cn.channels || {}).subtitle} onChange={v => up("channels", "subtitle", v)} />
            <p className="text-[9px] text-slate-400 bg-slate-50 p-2 rounded-sm">Los canales (telefono, WhatsApp, email) se toman automaticamente de la Configuracion general del sistema.</p>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="centros-ct2">
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-3"><CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><Building2 className="w-4 h-4 text-red-600" /> Centros en Contacto</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <SField label="Etiqueta" value={(cn.centros || {}).label} onChange={v => up("centros", "label", v)} hint="Vacio = Donde estamos" />
            <SField label="Titulo" value={(cn.centros || {}).title} onChange={v => up("centros", "title", v)} hint="Vacio = Nuestros centros" />
            <SField label="Subtitulo" value={(cn.centros || {}).subtitle} onChange={v => up("centros", "subtitle", v)} />
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="cta-ct">
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-3"><CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><MessageSquare className="w-4 h-4 text-red-600" /> CTA Final de Contacto</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <SField label="Titulo" value={(cn.cta_final || {}).title} onChange={v => up("cta_final", "title", v)} hint="Vacio = Estamos para ayudarte" />
            <SField label="Subtitulo" value={(cn.cta_final || {}).subtitle} onChange={v => up("cta_final", "subtitle", v)} type="textarea" />
            <SField label="Texto del boton" value={(cn.cta_final || {}).button_text} onChange={v => up("cta_final", "button_text", v)} hint="Vacio = Reservar Cita" />
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="config-ct">
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-3"><CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><Mail className="w-4 h-4 text-red-600" /> Configuracion del formulario</CardTitle>
          <CardDescription className="text-xs">Email destinatario y texto de confirmacion</CardDescription></CardHeader>
          <CardContent className="space-y-3">
            <SField label="Email destinatario" value={cn.form_dest_email} onChange={v => upRoot("form_dest_email", v)} hint="Email donde llegan las consultas. Si vacio, usa el email general de configuracion." />
            <SField label="Texto de confirmacion al usuario" value={cn.confirmation_text} onChange={v => upRoot("confirmation_text", v)} type="textarea" hint="Texto del email de confirmacion al usuario" />
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="seo-ct">
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-3"><CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><Eye className="w-4 h-4 text-red-600" /> SEO de Contacto</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <SField label="Title" value={(cn.seo || {}).title} onChange={v => up("seo", "title", v)} hint="Vacio = Contacto | ASCAUTO" />
            <SField label="Meta description" value={(cn.seo || {}).description} onChange={v => up("seo", "description", v)} type="textarea" />
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
}

/* ═══════════════════════════════════════════
   TRABAJA CON NOSOTROS EDITOR
   ═══════════════════════════════════════════ */
function TrabajaEditor({ content, setContent }) {
  const cn = content.trabaja || {};
  const up = (section, key, val) => {
    setContent(p => ({
      ...p,
      trabaja: { ...p.trabaja, [section]: { ...(p.trabaja || {})[section], [key]: val } }
    }));
  };
  const upRoot = (key, val) => {
    setContent(p => ({ ...p, trabaja: { ...p.trabaja, [key]: val } }));
  };

  return (
    <Tabs defaultValue="hero-tr" className="space-y-3">
      <TabsList className="bg-slate-100 rounded-sm h-auto flex-wrap gap-0.5 p-1">
        {[
          { v: "hero-tr", l: "Hero", i: PanelTop },
          { v: "quien-tr", l: "Quien buscamos", i: Users },
          { v: "areas-tr", l: "Areas", i: Briefcase },
          { v: "porque-tr", l: "Por que", i: Star },
          { v: "form-tr", l: "Formulario", i: MessageSquare },
          { v: "cta-tr", l: "CTA final", i: MessageSquare },
          { v: "config-tr", l: "Configuracion", i: Mail },
          { v: "seo-tr", l: "SEO", i: Eye },
        ].map(t => (
          <TabsTrigger key={t.v} value={t.v} className="rounded-sm text-[9px] uppercase tracking-wider font-semibold data-[state=active]:bg-white px-2.5 py-1">
            <t.i className="w-3 h-3 mr-1" />{t.l}
          </TabsTrigger>
        ))}
      </TabsList>

      <TabsContent value="hero-tr">
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-3"><CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><PanelTop className="w-4 h-4 text-red-600" /> Hero de Empleo</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <SField label="Etiqueta" value={(cn.hero || {}).label} onChange={v => up("hero", "label", v)} hint="Vacio = Empleo" />
            <SField label="Titulo (H1)" value={(cn.hero || {}).title} onChange={v => up("hero", "title", v)} hint="Vacio = Trabaja con nosotros" />
            <SField label="Subtitulo" value={(cn.hero || {}).subtitle} onChange={v => up("hero", "subtitle", v)} type="textarea" />
            <SField label="CTA primario" value={(cn.hero || {}).cta_primary} onChange={v => up("hero", "cta_primary", v)} hint="Vacio = Enviar candidatura" />
            <ImageUploadField label="Imagen de fondo" value={(cn.hero || {}).image_url} onChange={v => up("hero", "image_url", v)} />
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="quien-tr">
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-3"><CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><Users className="w-4 h-4 text-red-600" /> Quien buscamos</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <SField label="Etiqueta" value={(cn.quien_buscamos || {}).label} onChange={v => up("quien_buscamos", "label", v)} hint="Vacio = Perfiles" />
            <SField label="Titulo" value={(cn.quien_buscamos || {}).title} onChange={v => up("quien_buscamos", "title", v)} hint="Vacio = Personas que sumen de verdad" />
            <SField label="Texto" value={(cn.quien_buscamos || {}).text} onChange={v => up("quien_buscamos", "text", v)} type="textarea" rows={4} />
            <ImageUploadField label="Imagen lateral" value={(cn.quien_buscamos || {}).image_url} onChange={v => up("quien_buscamos", "image_url", v)} />
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="areas-tr">
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-3"><CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><Briefcase className="w-4 h-4 text-red-600" /> Areas profesionales</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <SField label="Etiqueta" value={(cn.areas || {}).label} onChange={v => up("areas", "label", v)} hint="Vacio = Areas profesionales" />
            <SField label="Titulo" value={(cn.areas || {}).title} onChange={v => up("areas", "title", v)} hint="Vacio = Areas en las que puedes encajar" />
            <IconItemEditor
              items={(cn.areas || {}).items || []}
              setItems={v => upRoot("areas", { ...(cn.areas || {}), items: v })}
              title="Areas"
              icon={Briefcase}
              fields={[
                { key: "title", label: "Titulo", type: "text" },
                { key: "description", label: "Descripcion", type: "textarea" },
                { key: "icon", label: "Icono", type: "text" },
              ]}
            />
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="porque-tr">
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-3"><CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><Star className="w-4 h-4 text-red-600" /> Por que trabajar en Ascauto</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <SField label="Etiqueta" value={(cn.por_que || {}).label} onChange={v => up("por_que", "label", v)} hint="Vacio = Ventajas" />
            <SField label="Titulo" value={(cn.por_que || {}).title} onChange={v => up("por_que", "title", v)} hint="Vacio = Por que unirte a ASCAUTO" />
            <SField label="Subtitulo" value={(cn.por_que || {}).subtitle} onChange={v => up("por_que", "subtitle", v)} />
            <IconItemEditor
              items={(cn.por_que || {}).items || []}
              setItems={v => upRoot("por_que", { ...(cn.por_que || {}), items: v })}
              title="Razones"
              icon={Star}
              fields={[
                { key: "title", label: "Titulo", type: "text" },
                { key: "description", label: "Descripcion", type: "textarea" },
                { key: "icon", label: "Icono", type: "text" },
              ]}
            />
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="form-tr">
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-3"><CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><MessageSquare className="w-4 h-4 text-red-600" /> Formulario de candidatura</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <SField label="Etiqueta" value={(cn.form || {}).label} onChange={v => up("form", "label", v)} hint="Vacio = Candidatura" />
            <SField label="Titulo" value={(cn.form || {}).title} onChange={v => up("form", "title", v)} hint="Vacio = Envianos tu candidatura" />
            <SField label="Subtitulo" value={(cn.form || {}).subtitle} onChange={v => up("form", "subtitle", v)} />
            <SField label="Texto de exito" value={(cn.form || {}).success_text} onChange={v => up("form", "success_text", v)} hint="Texto que aparece tras enviar la candidatura" />
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="cta-tr">
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-3"><CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><MessageSquare className="w-4 h-4 text-red-600" /> CTA Final</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <SField label="Titulo" value={(cn.cta_final || {}).title} onChange={v => up("cta_final", "title", v)} hint="Vacio = Queremos conocer tu perfil" />
            <SField label="Subtitulo" value={(cn.cta_final || {}).subtitle} onChange={v => up("cta_final", "subtitle", v)} type="textarea" />
            <SField label="Texto del boton" value={(cn.cta_final || {}).button_text} onChange={v => up("cta_final", "button_text", v)} hint="Vacio = Enviar candidatura" />
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="config-tr">
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-3"><CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><Mail className="w-4 h-4 text-red-600" /> Configuracion del formulario</CardTitle>
          <CardDescription className="text-xs">Email destinatario y texto de confirmacion</CardDescription></CardHeader>
          <CardContent className="space-y-3">
            <SField label="Email destinatario" value={cn.form_dest_email} onChange={v => upRoot("form_dest_email", v)} hint="Email donde llegan las candidaturas. Si vacio, usa el email de RRHH o el general." />
            <SField label="Texto de confirmacion al usuario" value={cn.confirmation_text} onChange={v => upRoot("confirmation_text", v)} type="textarea" hint="Texto del email de confirmacion al candidato" />
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="seo-tr">
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-3"><CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><Eye className="w-4 h-4 text-red-600" /> SEO de Trabaja con nosotros</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <SField label="Title" value={(cn.seo || {}).title} onChange={v => up("seo", "title", v)} hint="Vacio = Trabaja con nosotros | ASCAUTO" />
            <SField label="Meta description" value={(cn.seo || {}).description} onChange={v => up("seo", "description", v)} type="textarea" />
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
}


/* ═══════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════ */
export default function WebContentPage() {
  const { axiosAuth } = useAuth();
  const [content, setContent] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState("home");

  const fetchAll = useCallback(async () => {
    try {
      const [cnt, cfg] = await Promise.all([axios.get(`${API}/site-content`).then(r=>r.data), axios.get(`${API}/config`).then(r=>r.data)]);
      setContent(cnt); setConfig(cfg);
    } catch { toast.error("Error al cargar"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axiosAuth().put("/site-content", content);
      if (config) await axiosAuth().put("/config", config);
      toast.success("Contenido guardado");
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  if (loading||!content) return <div className="text-center py-12 text-slate-400">Cargando...</div>;

  const hero = content.hero||{};
  const ctaFinal = content.cta_final||{};
  const vis = config?.sections_visibility||{};
  const hc = content.header_config||{};

  const SECTIONS = [
    {key:"identity",label:"Identidad visual",icon:ImageIcon},
    {key:"header",label:"Cabecera publica",icon:Menu},
    {key:"home",label:"Home publica",icon:Monitor},
    {key:"conocenos",label:"Conócenos",icon:Users},
    {key:"contacto",label:"Contacto",icon:MessageSquare},
    {key:"trabaja",label:"Trabaja con nosotros",icon:Briefcase},
    {key:"services-pages",label:"Paginas de servicios",icon:Wrench},
    {key:"visibility",label:"Visibilidad y orden",icon:Eye},
  ];

  return (
    <div className="space-y-4" data-testid="webcontent-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 uppercase" data-testid="webcontent-title">Contenido Web</h1>
          <p className="text-xs text-slate-500 mt-0.5">Gestiona todos los contenidos de la web publica</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-red-600 hover:bg-red-700 text-white rounded-sm text-[10px] uppercase tracking-wider font-semibold h-8 px-4" data-testid="save-content-btn">
          <Save className="w-3.5 h-3.5 mr-1.5" /> {saving?"Guardando...":"Guardar Todo"}
        </Button>
      </div>

      {/* Section nav */}
      <div className="flex gap-1 flex-wrap">
        {SECTIONS.map(s=>(
          <Button key={s.key} variant={activeSection===s.key?"default":"outline"} onClick={()=>setActiveSection(s.key)}
            className={`rounded-sm text-[10px] uppercase tracking-wider font-semibold h-8 px-3 ${activeSection===s.key?"bg-slate-900 text-white":"border-slate-200 text-slate-600"}`}
            data-testid={`section-nav-${s.key}`}>
            <s.icon className="w-3.5 h-3.5 mr-1.5" /> {s.label}
          </Button>
        ))}
      </div>

      {/* ═══ IDENTIDAD VISUAL ═══ */}
      {activeSection==="identity" && <LogoManager />}

      {/* ═══ CABECERA PUBLICA ═══ */}
      {activeSection==="header" && (
        <div className="space-y-4">
          <Card className="border-slate-200 rounded-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><Menu className="w-4 h-4 text-red-600" /> Menu de navegacion</CardTitle>
              <CardDescription className="text-xs">Enlaces visibles en la cabecera publica. Puedes anadir, eliminar, reordenar y ocultar enlaces, incluyendo "Habla con tu asesor".</CardDescription>
            </CardHeader>
            <CardContent>
              <ItemEditor
                items={content.nav_links||[]}
                setItems={v=>setContent(p=>({...p,nav_links:v}))}
                title="Enlaces del menu"
                icon={Link2}
                fields={[
                  {key:"label",label:"Texto visible",type:"text",placeholder:"Ej: Habla con tu asesor"},
                  {key:"link",label:"Enlace destino",type:"text",placeholder:"/contacta-asesor"},
                  {key:"mobile_label",label:"Texto en movil (opcional, mas corto)",type:"text",placeholder:"Asesor"},
                  {key:"visible",label:"Visible en desktop",type:"switch",default:true},
                  {key:"show_mobile",label:"Visible en movil",type:"switch",default:true},
                  {key:"order",label:"Orden",type:"number",default:0},
                ]}
              />
            </CardContent>
          </Card>

          <Card className="border-slate-200 rounded-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><CalendarCheck className="w-4 h-4 text-red-600" /> Boton principal de la cabecera</CardTitle>
              <CardDescription className="text-xs">Boton CTA destacado (rojo) de la cabecera.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <SField label="Texto desktop" value={hc.cta_text} onChange={v=>setContent(p=>({...p,header_config:{...p.header_config,cta_text:v}}))} testid="header-cta-text" />
                <SField label="Texto movil" value={hc.cta_mobile_text} onChange={v=>setContent(p=>({...p,header_config:{...p.header_config,cta_mobile_text:v}}))} hint="Mas corto para movil (ej: Cita)" />
                <SField label="Enlace destino" value={hc.cta_link} onChange={v=>setContent(p=>({...p,header_config:{...p.header_config,cta_link:v}}))} />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Visible</Label>
                <Switch checked={hc.cta_visible!==false} onCheckedChange={v=>setContent(p=>({...p,header_config:{...p.header_config,cta_visible:v}}))} />
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 rounded-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><Building2 className="w-4 h-4 text-red-600" /> Marca en la cabecera</CardTitle>
              <CardDescription className="text-xs">Controla que elementos de marca se muestran junto al logo en la cabecera y el footer.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 border border-slate-200 rounded-sm">
                  <Label className="text-xs font-semibold text-slate-700">Mostrar logo</Label>
                  <Switch checked={(content.header_brand||{}).show_logo!==false} onCheckedChange={v=>setContent(p=>({...p,header_brand:{...p.header_brand,show_logo:v}}))} data-testid="brand-toggle-logo" />
                </div>
                <div className="flex items-center justify-between p-2 border border-slate-200 rounded-sm">
                  <Label className="text-xs font-semibold text-slate-700">Mostrar texto de marca</Label>
                  <Switch checked={(content.header_brand||{}).show_text!==false} onCheckedChange={v=>setContent(p=>({...p,header_brand:{...p.header_brand,show_text:v}}))} data-testid="brand-toggle-text" />
                </div>
                <div className="flex items-center justify-between p-2 border border-slate-200 rounded-sm">
                  <Label className="text-xs font-semibold text-slate-700">Mostrar subtitulo / claim</Label>
                  <Switch checked={(content.header_brand||{}).show_tagline!==false} onCheckedChange={v=>setContent(p=>({...p,header_brand:{...p.header_brand,show_tagline:v}}))} data-testid="brand-toggle-tagline" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <SField label="Texto principal de marca" value={(content.header_brand||{}).brand_text} onChange={v=>setContent(p=>({...p,header_brand:{...p.header_brand,brand_text:v}}))} hint="Vacio = nombre de la empresa" testid="brand-text-input" />
                <SField label="Subtitulo / Claim" value={(content.header_brand||{}).tagline_text} onChange={v=>setContent(p=>({...p,header_brand:{...p.header_brand,tagline_text:v}}))} hint="Vacio = tagline de identidad visual" testid="brand-tagline-input" />
              </div>
            </CardContent>
          </Card>

          {/* Footer links */}
          <Card className="border-slate-200 rounded-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><Link2 className="w-4 h-4 text-red-600" /> Enlaces del Footer</CardTitle>
              <CardDescription className="text-xs">Enlaces rapidos que aparecen en el pie de pagina de todas las paginas. Los enlaces legales (privacidad, aviso legal, cookies) son fijos.</CardDescription>
            </CardHeader>
            <CardContent>
              <ItemEditor
                items={content.footer_links||[{label:"Inicio",link:"/"},{label:"Vehiculos",link:"/vehiculos"},{label:"Habla con tu Asesor",link:"/contacta-asesor"},{label:"Reservar Cita",link:"/cita"}]}
                setItems={v=>setContent(p=>({...p,footer_links:v}))}
                title="Enlaces del footer"
                icon={Link2}
                fields={[
                  {key:"label",label:"Texto visible",type:"text",placeholder:"Ej: Reservar Cita"},
                  {key:"link",label:"Enlace destino",type:"text",placeholder:"/cita"},
                ]}
              />
            </CardContent>
          </Card>

          {/* Legal bottom bar text */}
          <Card className="border-slate-200 rounded-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><FileText className="w-4 h-4 text-red-600" /> Textos del Footer</CardTitle>
              <CardDescription className="text-xs">Texto descriptivo y legal que aparece en el pie de pagina.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <SField label="Texto descriptivo del footer" value={content.footer_description} onChange={v=>setContent(p=>({...p,footer_description:v}))} type="textarea" hint="Texto que aparece bajo el logo en el footer. Vacio = usa el texto de Configuracion > Datos del Taller" />
              <SField label="Texto legal inferior" value={content.footer_legal_text} onChange={v=>setContent(p=>({...p,footer_legal_text:v}))} hint="Vacio = Ano + nombre empresa + Servicio Oficial Stellantis" />
            </CardContent>
          </Card>
        </div>
      )}
      {activeSection==="home" && (
        <Tabs defaultValue="hero" className="space-y-3">
          <TabsList className="bg-slate-100 rounded-sm h-auto flex-wrap gap-0.5 p-1">
            {[
              {v:"hero",l:"Hero",i:PanelTop},{v:"quick",l:"Accesos",i:Zap},{v:"official",l:"S. Oficial",i:Award},
              {v:"services",l:"Servicios",i:Wrench},{v:"trust",l:"Confianza",i:BarChart3},{v:"vehicles",l:"Vehículos",i:Car},
              {v:"reviews",l:"Opiniones",i:Star},{v:"promos",l:"Promos",i:BadgeCheck},{v:"centers-home",l:"Centros",i:Building2},
              {v:"cta",l:"CTA",i:MessageSquare},{v:"floating",l:"Flotantes",i:MessageCircle},
            ].map(t=>(
              <TabsTrigger key={t.v} value={t.v} className="rounded-sm text-[9px] uppercase tracking-wider font-semibold data-[state=active]:bg-white px-2.5 py-1"><t.i className="w-3 h-3 mr-1" />{t.l}</TabsTrigger>
            ))}
          </TabsList>

          {/* HERO */}
          <TabsContent value="hero" className="space-y-4">
            {/* HERO PRINCIPAL - SLIDES */}
            <Card className="border-slate-200 rounded-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <Monitor className="w-4 h-4 text-red-600" />
                  <CardTitle className="text-sm uppercase tracking-wider font-semibold">Hero Principal — Slider</CardTitle>
                  <span className="text-[8px] uppercase tracking-wider font-bold text-green-600 bg-green-100 px-1.5 py-0.5 rounded-sm">Prioridad</span>
                </div>
                <CardDescription className="text-xs">Estos slides se muestran en la portada de la home. Si hay al menos 1 slide activo, se usa el slider. Si no hay ninguno, se muestra el fallback de abajo.</CardDescription>
              </CardHeader>
              <CardContent>
                <HeroSlidesEditor />
              </CardContent>
            </Card>

            {/* HERO FALLBACK */}
            <Card className="border-slate-200 rounded-sm border-dashed">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <PanelTop className="w-4 h-4 text-slate-400" />
                  <CardTitle className="text-sm uppercase tracking-wider font-semibold text-slate-500">Hero Fallback</CardTitle>
                  <span className="text-[8px] uppercase tracking-wider font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-sm">Solo sin slides</span>
                </div>
                <CardDescription className="text-xs">Este contenido solo se muestra si no hay slides activos arriba. Funciona como respaldo.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <SField label="Titulo fallback" value={hero.title} onChange={v=>setContent(p=>({...p,hero:{...p.hero,title:v}}))} testid="hero-title-input" />
                <SField label="Subtitulo fallback" value={hero.subtitle} onChange={v=>setContent(p=>({...p,hero:{...p.hero,subtitle:v}}))} type="textarea" testid="hero-subtitle-input" />
                <ImageUploadField label="Imagen de fondo (fallback)" value={hero.bg_image} onChange={v=>setContent(p=>({...p,hero:{...p.hero,bg_image:v}}))} hint="Vacio = fondo oscuro por defecto" />
              </CardContent>
            </Card>
          </TabsContent>

          {/* ACCESOS */}
          <TabsContent value="quick">
            <Card className="border-slate-200 rounded-sm">
              <CardHeader className="pb-3"><CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><Zap className="w-4 h-4 text-red-600" />Accesos Rapidos</CardTitle>
                <CardDescription className="text-xs">Botones grandes debajo del hero. El marcado "primary" destaca en rojo.</CardDescription></CardHeader>
              <CardContent>
                <QuickAccessEditor items={content.quick_access||[]} setItems={v=>setContent(p=>({...p,quick_access:v}))} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* SERVICIO OFICIAL */}
          <TabsContent value="official">
            <Card className="border-slate-200 rounded-sm">
              <CardHeader className="pb-3"><CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><Award className="w-4 h-4 text-red-600" />Servicio Oficial</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <SField label="Titulo" value={content.advantages_section_title} onChange={v=>setContent(p=>({...p,advantages_section_title:v}))} testid="advantages-title" />
                  <SField label="Subtitulo" value={content.advantages_section_subtitle} onChange={v=>setContent(p=>({...p,advantages_section_subtitle:v}))} testid="advantages-subtitle" />
                </div>
                <IconItemEditor items={content.advantages||[]} setItems={v=>setContent(p=>({...p,advantages:v}))} title="Ventajas" icon={Award}
                  fields={[{key:"title",label:"Titulo",type:"text"},{key:"description",label:"Descripcion",type:"textarea"},{key:"icon",label:"Icono",type:"text"}]} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* SERVICIOS */}
          <TabsContent value="services">
            <Card className="border-slate-200 rounded-sm">
              <CardHeader className="pb-3"><CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><Wrench className="w-4 h-4 text-red-600" />Servicios Destacados</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <SField label="Titulo" value={content.services_section_title} onChange={v=>setContent(p=>({...p,services_section_title:v}))} testid="services-title" />
                  <SField label="Subtitulo" value={content.services_section_subtitle} onChange={v=>setContent(p=>({...p,services_section_subtitle:v}))} testid="services-subtitle" />
                </div>
                <IconItemEditor items={content.services||[]} setItems={v=>setContent(p=>({...p,services:v}))} title="Servicios" icon={Wrench}
                  fields={[{key:"title",label:"Titulo",type:"text"},{key:"description",label:"Descripcion",type:"textarea"},{key:"icon",label:"Icono",type:"text"}]} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* CONFIANZA */}
          <TabsContent value="trust">
            <Card className="border-slate-200 rounded-sm">
              <CardHeader className="pb-3"><CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><BarChart3 className="w-4 h-4 text-red-600" />Confianza y Trayectoria</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <SField label="Titulo" value={content.trust_section_title} onChange={v=>setContent(p=>({...p,trust_section_title:v}))} />
                  <SField label="Subtitulo" value={content.trust_section_subtitle} onChange={v=>setContent(p=>({...p,trust_section_subtitle:v}))} />
                </div>
                <ItemEditor items={content.trust_stats||[]} setItems={v=>setContent(p=>({...p,trust_stats:v}))} title="KPIs" icon={BarChart3}
                  fields={[{key:"value",label:"Valor (ej: 40+, 100%)",type:"text"},{key:"label",label:"Etiqueta",type:"text"},{key:"description",label:"Descripcion",type:"text"}]} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* VEHICULOS */}
          <TabsContent value="vehicles">
            <Card className="border-slate-200 rounded-sm">
              <CardHeader className="pb-3"><CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><Car className="w-4 h-4 text-red-600" />Vehiculos</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <SField label="Titulo" value={content.vehicles_section_title} onChange={v=>setContent(p=>({...p,vehicles_section_title:v}))} />
                <SField label="Subtitulo" value={content.vehicles_section_subtitle} onChange={v=>setContent(p=>({...p,vehicles_section_subtitle:v}))} />
                <SField label="Texto descriptivo" value={content.vehicles_section_description} onChange={v=>setContent(p=>({...p,vehicles_section_description:v}))} type="textarea" />
              </CardContent>
            </Card>
          </TabsContent>

          {/* OPINIONES */}
          <TabsContent value="reviews">
            <Card className="border-slate-200 rounded-sm">
              <CardHeader className="pb-3"><CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><Star className="w-4 h-4 text-red-600" />Opiniones</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <SField label="Titulo" value={content.testimonials_section_title} onChange={v=>setContent(p=>({...p,testimonials_section_title:v}))} />
                  <SField label="Subtitulo" value={content.testimonials_section_subtitle} onChange={v=>setContent(p=>({...p,testimonials_section_subtitle:v}))} />
                </div>
                <ItemEditor items={content.testimonials||[]} setItems={v=>setContent(p=>({...p,testimonials:v}))} title="Opiniones" icon={Star}
                  fields={[{key:"name",label:"Nombre",type:"text"},{key:"location",label:"Localidad",type:"text"},{key:"text",label:"Texto",type:"textarea"},{key:"rating",label:"Valoracion (1-5)",type:"number",default:5}]} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* CTA FINAL */}
          <TabsContent value="cta">
            <Card className="border-slate-200 rounded-sm">
              <CardHeader className="pb-3"><CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><MessageSquare className="w-4 h-4 text-red-600" />CTA Final</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <SField label="Titulo" value={ctaFinal.title} onChange={v=>setContent(p=>({...p,cta_final:{...p.cta_final,title:v}}))} />
                <SField label="Subtitulo" value={ctaFinal.subtitle} onChange={v=>setContent(p=>({...p,cta_final:{...p.cta_final,subtitle:v}}))} type="textarea" />
                <SField label="Texto del boton" value={ctaFinal.button_text} onChange={v=>setContent(p=>({...p,cta_final:{...p.cta_final,button_text:v}}))} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* PROMOCIONES en HOME */}
          <TabsContent value="promos">
            <Card className="border-slate-200 rounded-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-red-600" />Promociones en la Home</CardTitle>
                <CardDescription className="text-xs">Textos de la seccion de promociones en la pagina principal. Las promociones se gestionan en el modulo "Promociones Postventa".</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <SField label="Titulo de la seccion" value={content.promotions_section_title} onChange={v=>setContent(p=>({...p,promotions_section_title:v}))} hint="Vacio = Promociones Postventa" />
                <SField label="Subtitulo / Etiqueta" value={content.promotions_section_subtitle} onChange={v=>setContent(p=>({...p,promotions_section_subtitle:v}))} hint="Vacio = Ofertas activas" />
                <p className="text-[9px] text-slate-400 bg-slate-50 p-2 rounded-sm">Las promociones se crean y gestionan desde <strong>Promociones Postventa</strong> en el menu lateral. Aqui solo se configuran los textos del bloque en la Home.</p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* CENTROS en HOME */}
          <TabsContent value="centers-home">
            <Card className="border-slate-200 rounded-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><Building2 className="w-4 h-4 text-red-600" />Sedes y Contacto</CardTitle>
                <CardDescription className="text-xs">Textos de la seccion de centros/sedes en la pagina principal. Los centros se gestionan en Configuracion.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <SField label="Titulo de la seccion" value={content.centers_section_title} onChange={v=>setContent(p=>({...p,centers_section_title:v}))} hint="Vacio = Nuestros Centros" />
                <SField label="Subtitulo / Etiqueta" value={content.centers_section_subtitle} onChange={v=>setContent(p=>({...p,centers_section_subtitle:v}))} hint="Vacio = Visitanos" />
                <p className="text-[9px] text-slate-400 bg-slate-50 p-2 rounded-sm">Los centros se configuran en <strong>Configuracion &gt; Centros</strong>. Aqui solo se configuran los textos del bloque en la Home.</p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* FLOTANTES */}
          <TabsContent value="floating">
            <Card className="border-slate-200 rounded-sm">
              <CardHeader className="pb-3"><CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><MessageCircle className="w-4 h-4 text-red-600" />CTAs Flotantes</CardTitle></CardHeader>
              <CardContent>
                {(()=>{const fc=content.floating_ctas||{}; return (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-3 p-4 border border-slate-200 rounded-sm">
                      <div className="flex items-center justify-between"><Label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5"><MessageCircle className="w-3.5 h-3.5 text-green-600" />WhatsApp</Label>
                        <Switch checked={fc.whatsapp!==false} onCheckedChange={v=>setContent(p=>({...p,floating_ctas:{...p.floating_ctas,whatsapp:v}}))} /></div>
                      <SField label="Etiqueta" value={fc.whatsapp_label} onChange={v=>setContent(p=>({...p,floating_ctas:{...p.floating_ctas,whatsapp_label:v}}))} />
                      <SField label="Mensaje predefinido" value={fc.whatsapp_message} onChange={v=>setContent(p=>({...p,floating_ctas:{...p.floating_ctas,whatsapp_message:v}}))} hint="Texto que aparece pre-escrito al abrir el chat. Ej: Hola, me gustaria pedir informacion..." />
                      <p className="text-[9px] text-slate-400 bg-slate-50 p-2 rounded-sm">El numero de WhatsApp se configura en <strong>Configuracion &gt; Datos de la empresa &gt; WhatsApp General</strong>.</p>
                    </div>
                    <div className="space-y-3 p-4 border border-slate-200 rounded-sm">
                      <div className="flex items-center justify-between"><Label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5"><CalendarCheck className="w-3.5 h-3.5 text-red-600" />Pedir cita</Label>
                        <Switch checked={fc.cita!==false} onCheckedChange={v=>setContent(p=>({...p,floating_ctas:{...p.floating_ctas,cita:v}}))} /></div>
                      <SField label="Etiqueta" value={fc.cita_label} onChange={v=>setContent(p=>({...p,floating_ctas:{...p.floating_ctas,cita_label:v}}))} />
                    </div>
                  </div>
                );})()}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* ═══ PAGINAS DE SERVICIOS ═══ */}
      {activeSection==="services-pages" && (
        <Tabs defaultValue="mecanica" className="space-y-3">
          <TabsList className="bg-slate-100 rounded-sm h-auto flex-wrap gap-0.5 p-1">
            <TabsTrigger value="mecanica" className="rounded-sm text-[9px] uppercase tracking-wider font-semibold data-[state=active]:bg-white px-2.5 py-1"><Wrench className="w-3 h-3 mr-1" />Mecanica</TabsTrigger>
            <TabsTrigger value="chapa" className="rounded-sm text-[9px] uppercase tracking-wider font-semibold data-[state=active]:bg-white px-2.5 py-1"><Paintbrush className="w-3 h-3 mr-1" />Chapa y pintura</TabsTrigger>
            <TabsTrigger value="recambios" className="rounded-sm text-[9px] uppercase tracking-wider font-semibold data-[state=active]:bg-white px-2.5 py-1"><Package className="w-3 h-3 mr-1" />Recambios</TabsTrigger>
          </TabsList>
          <TabsContent value="mecanica"><ServicePageEditor slug="mecanica" label="Mecánica y mantenimiento" icon={Wrench} content={content} setContent={setContent} /></TabsContent>
          <TabsContent value="chapa"><ServicePageEditor slug="chapa" label="Chapa y pintura" icon={Paintbrush} content={content} setContent={setContent} /></TabsContent>
          <TabsContent value="recambios"><ServicePageEditor slug="recambios" label="Recambios originales" icon={Package} content={content} setContent={setContent} /></TabsContent>
        </Tabs>
      )}

      {/* ═══ CONOCENOS ═══ */}
      {activeSection==="conocenos" && (
        <ConocenosEditor content={content} setContent={setContent} />
      )}

      {/* ═══ CONTACTO ═══ */}
      {activeSection==="contacto" && (
        <ContactoEditor content={content} setContent={setContent} />
      )}

      {/* ═══ TRABAJA CON NOSOTROS ═══ */}
      {activeSection==="trabaja" && (
        <TrabajaEditor content={content} setContent={setContent} />
      )}

      {/* ═══ VISIBILIDAD ═══ */}
      {activeSection==="visibility" && (
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-3"><CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><Eye className="w-4 h-4 text-red-600" />Visibilidad de secciones de la Home</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {[{key:"hero",label:"Hero / Cabecera",icon:PanelTop},{key:"quick_access",label:"Accesos Rapidos",icon:Zap},{key:"advantages",label:"Servicio Oficial",icon:Award},{key:"services",label:"Servicios Destacados",icon:Wrench},
                {key:"trust",label:"Confianza y Trayectoria",icon:BarChart3},{key:"vehicles",label:"Vehiculos Usados y Km0",icon:Car},{key:"testimonials",label:"Opiniones",icon:Star},{key:"promotions",label:"Promociones Postventa",icon:BadgeCheck},
                {key:"conocenos",label:"Bloque Conocenos",icon:Users},{key:"centers",label:"Sedes y Contacto",icon:Building2},{key:"cta_final",label:"CTA Final",icon:MessageSquare},{key:"brands",label:"Marcas Oficiales",icon:Award}].map(s=>(
                <div key={s.key} className="flex items-center justify-between p-3 border border-slate-200 rounded-sm bg-white">
                  <div className="flex items-center gap-2"><s.icon className="w-4 h-4 text-slate-400" /><span className="text-sm font-medium text-slate-800">{s.label}</span></div>
                  <div className="flex items-center gap-2">
                    <Badge variant={vis[s.key]!==false?"default":"outline"} className={`text-[9px] uppercase ${vis[s.key]!==false?"bg-green-100 text-green-700":"text-slate-400"}`}>{vis[s.key]!==false?"Visible":"Oculta"}</Badge>
                    <Switch checked={vis[s.key]!==false} onCheckedChange={v=>setConfig(p=>({...p,sections_visibility:{...p.sections_visibility,[s.key]:v}}))} />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
